import type { CalibrationCaseData } from "@/hooks/queries/calibration-status-types";

// Story 8.10 (AC3) — sequential case start, derived ONCE.
//
// The board's card gating and the scoring screen's deep-link guard must agree:
// two independent derivations would drift the moment either is edited, and the
// failure is silent (a card offers a case the screen then refuses, or worse the
// reverse). Both call `caseAccess`.
//
// Rule-06 note: this is AFFORDANCE gating, not business logic. The FE derives
// which CTA is live from server-owned case status and invents no rule the server
// doesn't hold; the server never ordered cases and still doesn't, and it stays
// authoritative on what actually matters (P0-CAL-03/04 refuse with 409 regardless
// of what the client shows).

/**
 * The next case to open: the first non-`scored` case by `order`. Returns null when
 * all five are scored (calibration complete). Pure + order-independent — sorts by
 * `order` rather than trusting wire order.
 *
 * Lives here (Story 8.10) rather than in `CalibrationCasesScreen` so the board and
 * the scoring screen share ONE derivation without a module cycle; the screen
 * re-exports it, so its original importers are unchanged.
 */
export function nextCase(cases: CalibrationCaseData[]): CalibrationCaseData | null {
  return (
    [...cases].sort((a, b) => a.order - b.order).find((c) => c.status !== "scored") ?? null
  );
}

/**
 * PROVEN board completion: ≥1 case AND every case `scored` — an empty/malformed
 * board is NOT complete. Drives the board's "All complete" affordance (Story 8.8
 * AC6) and the scoring view's 5th-case route to `/calibration/complete` (AC5).
 *
 * Lives here (moved from `CalibrationCasesScreen` at Story 8.10 review) next to
 * `nextCase`/`caseAccess` so the board and the scoring screen share ONE derivation
 * without the scoring module importing the board module; the board re-exports it,
 * so its existing importers are unchanged. Pure + testable.
 */
export function isBoardComplete(cases: CalibrationCaseData[]): boolean {
  return cases.length > 0 && cases.every((c) => c.status === "scored");
}

export type CaseAccess =
  /** The next unscored case by `order` — the one case a clinician may open. */
  | "startable"
  /** Unscored, but an earlier case is still unscored. */
  | "locked"
  /** Already scored — belongs to the read-only case log, not the workspace. */
  | "scored"
  /** No such case on this board (unknown/crafted id, or the board failed to load). */
  | "unknown";

/**
 * How the given case may be reached, from the board's own `cases[]`.
 *
 * Pure and order-independent (`nextCase` sorts by `order`, never trusting wire
 * order). An empty board yields "unknown" for every id — an absent board is not
 * evidence that a case is startable.
 */
export function caseAccess(cases: CalibrationCaseData[], caseId: string | undefined): CaseAccess {
  if (!caseId) return "unknown";
  const target = cases.find((c) => c.case_id === caseId);
  if (!target) return "unknown";
  if (target.status === "scored") return "scored";
  return nextCase(cases)?.case_id === caseId ? "startable" : "locked";
}

/**
 * Why a locked case can't be opened, phrased for the clinician.
 *
 * Visible TEXT, never a native `title`: the iPad PWA is the primary clinical
 * surface and `title` is mouse-only there (the standing gh-555/gh-568 finding).
 */
export function lockedReason(cases: CalibrationCaseData[]): string {
  const next = nextCase(cases);
  return next ? `Complete case ${next.order} first.` : "Not available yet.";
}
